import React, {useEffect} from "react";
/** @jsxImportSource @emotion/react */
import {css, useTheme} from "@emotion/react";
import styled from "@emotion/styled";
import {useMobileDetect} from "../../utils/Hooks";
import {
    BROADCAST_ON_PASS_MOBILE_HEADER_TITLE,
    getImageUrl,
    useApplicationContext,
} from "../../context/ApplicationContextProvider";
import {HEADER_TYPE_BASE} from "../_common/section/Header";
import {FOOTER_TYPE_NONE} from "../_common/section/Footer";

const ContainerInfoViewer = () => {
    // ApplicationContext
    useApplicationContext({
        header: {
            type: HEADER_TYPE_BASE,
        },
        footer: {
            type: FOOTER_TYPE_NONE,
        },
    });

    const {is_mobile} = useMobileDetect();
    const {broadcast} = useApplicationContext();

    // css style
    const {css, view} = useComponentStyle(is_mobile);

    useEffect(() => {
        broadcast.send(BROADCAST_ON_PASS_MOBILE_HEADER_TITLE, "영상 관련 문제 해결 방법 안내");
    }, [broadcast]);

    return (
        <div css={css.info_viewer_container_css}>
            <view.InfoViewerTitle>{!is_mobile && <span>영상 재생관련 문제 해결 방법 안내</span>}</view.InfoViewerTitle>
            <view.InfoBox>
                <p className="box-sub-title">안녕하세요 베어유입니다 🙂</p>
                <p className="box-title">
                    <span>영상 재생</span>에 문제가 있으신가요?
                </p>
            </view.InfoBox>
            <view.InfoWrapper>
                <p className="info-hint">아래 간단한 방법으로 문제를 해결해 보세요.</p>
                <p className="info-title">
                    <span className="numbering">첫 번째! </span>
                    [캐쉬 및 쿠키 초기화]
                </p>
                <p className="info-description">
                    1. 사용하고 계신 인터넷 브라우저 캐쉬 및 쿠키를 초기화 후 재부팅
                    <br />
                    2. 베어유 로그인 후 다시 재생
                </p>
                <p className="info-title">
                    <span className="numbering">두 번째! </span>
                    [영상 CDN 설정]
                </p>
                <p className="info-description">
                    1.&nbsp;
                    <a href="http://player.vimeo.com/flags?p=general" target="_blank" rel="noreferrer">
                        <span
                            style={{
                                color: "blue",
                            }}>
                            http://player.vimeo.com/flags?p=general
                        </span>
                    </a>
                    에 접속
                    <br />
                    <img src={getImageUrl("image/service/info_viewer/vimeo_info.png")} alt="비메오 문제 해결" />
                    2. <span className="bold">CDN Priority</span>에서 <span className="bold">Disabled</span> 로 설정되어
                    있는 부분을 클릭
                    <br />
                    3. <span className="bold">Disabled</span>를 <span className="bold">Akamai</span> 로 설정 후 창을
                    닫는다.
                    <br />
                    4. 베어유 홈페이지 재접속 후 영상 시청
                    <br />
                    5. 그래도 재생이 안 될 경우 3번 방법에서 <span className="bold">Fastly</span> 로 변경 후 재시청
                </p>
                <p className="info-hint">* 사용하시는 브라우저에 따라 다를 수 있기 떄문에 꼭 확인을 해주세요.</p>
            </view.InfoWrapper>
        </div>
    );
};

export default ContainerInfoViewer;

export const useComponentStyle = (is_mobile) => {
    const theme = useTheme();
    return {
        css: {
            info_viewer_container_css: css({
                width: is_mobile ? "100%" : "70%",
                minWidth: "260px",
                margin: "auto",
                transition: "all .2s",
                "& .numbering": {
                    color: theme.colors.info_title_numbering,
                },
                "& .bold": {
                    fontWeight: "bold",
                },
            }),
        },
        view: {
            InfoViewerTitle: styled.div({
                textAlign: "center",
                width: "100%",
                fontSize: theme.font_size.f21,
                fontWeight: "700",
                margin: "3.5em 0 1.5em 0",
            }),
            InfoBox: styled.div({
                backgroundColor: theme.colors.info_box_background,
                padding: is_mobile ? "2.5em 0" : "3em 0",
                borderRadius: ".7em",
                color: theme.colors.w,
                textAlign: "center",
                "& > p": {
                    margin: 0,
                },
                "& > .box-sub-title": {
                    fontSize: is_mobile ? theme.font_size.f14 : theme.font_size.f16,
                    fontWeight: "500",
                },
                "& > .box-title": {
                    fontSize: is_mobile ? theme.font_size.f20 : theme.font_size.f24,
                    fontWeight: "700",
                    marginTop: ".2em",
                    transition: "all .2s",
                    "@media (max-width: 280px)": {
                        fontSize: theme.font_size.f17,
                    },
                    span: {
                        color: theme.colors.info_title,
                    },
                },
            }),
            InfoWrapper: styled.div({
                marginTop: is_mobile ? "1em" : "2em",
                "& p": {
                    margin: 0,
                },
                "& > .info-hint": {
                    textAlign: "center",
                    fontSize: is_mobile ? theme.font_size.f15 : theme.font_size.f17,
                    fontWeight: "400",
                },
                "& .info-title": {
                    marginTop: "2em",
                    fontSize: is_mobile ? theme.font_size.f17 : theme.font_size.f19,
                    fontWeight: "700",
                },
                "& .info-description": {
                    marginTop: "1em",
                    lineHeight: "1.5em",
                    fontSize: is_mobile ? theme.font_size.f15 : theme.font_size.f17,
                },
                "& img": {
                    margin: ".2em 0",
                    width: "100%",
                    border: `1px solid ${theme.colors.lg2}`,
                },
                "& p:last-child": {
                    margin: "3em 0 2em 0",
                },
            }),
        },
    };
};
